import { localNow, parseServerTime } from "@/filters/DateAndTime";
import { patientNamespace } from "@/store/modules/patient";
import { userNamespace } from "@/store/modules/user";
import _ from "lodash";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { Colors } from "./util";

const TEST_POLLING_INTERVAL = 30000;

@Component({})
export default class NotificationTest extends Vue {
  @Getter("pagerID", userNamespace)
  pagerID!: string;

  @Action("pagingTestStart", userNamespace)
  pagingTestStart!: Function;

  @Action("pagingTestStop", userNamespace)
  pagingTestStop!: Function;

  @Action("pagingTestNote", userNamespace)
  pagingTestNote!: Function;

  @Action("pagingTestComplete", userNamespace)
  pagingTestComplete!: Function;

  @Action("pagingTestResult", userNamespace)
  pagingTestResult!: Function;

  @Action("pagingReceived", userNamespace)
  pagingReceived!: Function;

  @Getter("isPagingTest", patientNamespace)
  isPagingTest!: boolean;

  @Getter("isPagingTestActive", patientNamespace)
  isPagingTestActive!: string;

  @Action("setIsPagingTest", patientNamespace)
  setIsPagingTest!: Function;

  @Prop(Boolean)
  helpOpen!: boolean;

  @Prop(Boolean)
  resultsOpen!: boolean;

  isHelpOpen: boolean = false;

  isInfoOpen: boolean = false;
  isResultsOpen: boolean = true;

  pagerIdText = "";

  noteText = "";

  showComplete = false;

  showThankYou = false;

  testResults: any = { sentPages: [] };

  interval: any = null;

  summaryConfig = [
    { key: "testCount", value: "Count" },
    { key: "testsIncomplete", value: "Incomplete" },
    { key: "passPercentString", value: "Passed" },
    { key: "averageLatency", value: "Avg Response (s)" },
    { key: "maxSeconds", value: "Max Response (s)" }
  ];
  get isPassing(): boolean {
    return _.get(this, "testResults.scoreCard.isPassing", false);
  }

  get hasResults(): boolean {
    const testCount = _.get(this, "testResults.summary.testCount", 0);
    const hasNotes = _.get(this, "testResults.notes", null) !== null;
    return testCount || hasNotes;
  }

  get statusExplanation(): string {
    return _.get(this, "testResults.scoreCard.statusExplanation", "");
  }

  get isPassingFidelity(): boolean {
    return _.get(this, "testResults.scoreCard.isPassingFidelity", false);
  }

  get isPassingLatency(): boolean {
    return _.get(this, "testResults.scoreCard.isPassingLatency", false);
  }

  get isPassingSecurity(): boolean {
    return _.get(this, "testResults.scoreCard.isPassingSecurity", false);
  }

  get showingPopup() {
    return this.$route.query.popupName;
  }

  get isPagerIdValid() {
    return (
      this.pagerIdText !== undefined &&
      this.pagerIdText !== null &&
      this.pagerIdText !== ""
    );
  }

  get isUserPagerIdValid() {
    return (
      this.pagerID !== null && this.pagerID !== "" && this.pagerID !== undefined
    );
  }

  get isButtonActive() {
    return this.isPagerIdValid && !this.showComplete;
  }

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  get hasPendingTests() {
    return this.testResults.sentPages != null && this.testResults.sent;
  }

  get testIconColor() {
    return this.isPagingTest ? Colors.Red : Colors.TestGreen;
  }

  get colorRed() {
    return Colors.Red;
  }

  get colorGreen() {
    return Colors.TestGreen;
  }

  get colorBlue() {
    return Colors.LogoBlue;
  }

  get testIcon() {
    return this.isPagingTest ? "call_end" : "phone_in_talk";
  }

  get testText() {
    return this.isPagingTest ? "Stop pages" : "Start test";
  }

  get testMessage() {
    return this.isPagingTest ? "Test session Active" : "";
  }

  toggleHelpOpen() {
    this.isHelpOpen = !this.isHelpOpen;
  }

  toggleResultsOpen() {
    this.isResultsOpen = !this.isResultsOpen;
  }

  toggleInfoOpen() {
    this.isInfoOpen = !this.isInfoOpen;
  }

  async addNote() {
    if (this.noteText != null && this.noteText !== "") {
      this.pagingTestNote(this.noteText);
      this.updateResults();
      this.noteText = "";
    }
  }

  private startPolling() {
    if (this.interval) {
      // Avoid setting up double polling
      return;
    }
    this.interval = setInterval(() => {
      this.updateResults();
    }, TEST_POLLING_INTERVAL);
  }

  beforeDestroy() {
    this.stopPolling();
  }

  private stopPolling() {
    clearInterval(this.interval);
    this.interval = null;
  }

  async mounted() {
    if (this.isPagingTest == true) {
      await this.updateResults();
      this.pagerIdText = this.testResults.pagerID;
      this.startPolling();
      const tests = this.testResults.sentPages;
      const end = tests.length - 1;
      // based on how long ago the last page was sent (TODO: may be weird in other timezones...)
      const isStillPaging =
        end > 0 &&
        tests[end] !== null &&
        tests[end].timestamp != null &&
        localNow().diff(tests[end].timestamp, "second", false) < 120;
      if (!this.isPagingTestActive || !isStillPaging) {
        this.showComplete = true;
      }
    } else if (this.isUserPagerIdValid) {
      this.pagerIdText = this.pagerID;
    }
    if (this.helpOpen) this.isHelpOpen = true;
  }

  toggetTest() {
    this.isPagingTest = !this.isPagingTest;
  }

  displayTime(time: string) {
    const t = parseServerTime(time.replace(" ", "T").split(".")[0]);
    return (
      t.format(!this.isMobile ? "MMM M, YYYY" : "M/D/YY") +
      " " +
      t.format(!this.isMobile ? "hh:mm.ss A" : "h:mm.ss a")
    );
  }
  summaryText(key: string) {
    const summ = this.summaryConfig.filter(f => f.key == key);
    return summ.length > 0 && summ[0].value != null ? summ[0].value : "";
  }

  get displaySummary() {
    return this.testResults.summary.filter(
      (f: any) => this.summaryText(f.key) != null
    );
  }

  async togglePagingTest() {
    // Not active -> Start
    if (!this.isPagingTest && this.isPagerIdValid) {
      await this.pagingTestStart(this.pagerIdText);
      this.updateResults();
      setTimeout(this.updateResults, 2000);
      this.startPolling();
      this.setIsPagingTest(true);
      if (!this.resultsOpen) this.isResultsOpen = true;
    } else if (this.isPagingTest) {
      // Active -> stop
      this.pagingTestStop();
      this.showComplete = true;
    }
  }

  async updateResults() {
    this.testResults = await this.pagingTestResult();
  }

  async completeTest(index: number) {
    await this.pagingReceived(index);
    this.updateResults();
  }
  async completeSession() {
    this.pagingTestComplete();
    this.setIsPagingTest(false);
    this.pagerIdText = this.isUserPagerIdValid ? this.pagerID : "";
    this.showComplete = false;
    this.stopPolling();
    this.testResults = { sentPages: [] };
    this.showThankYou = true;
  }
}
